import { useEffect, type FC, type PropsWithChildren } from 'react'

import { ExternalLink, Icon, LoaderCircle } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import CartIcon from 'mdi-react/CartIcon'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon'
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon'
import PhoneOffIcon from 'mdi-react/PhoneOffIcon'
import PhonePlusIcon from 'mdi-react/PhonePlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { GUIDE_FILE_PATHS } from '@/app-constants'
import type { ColorMode } from '@/components/RouterLink'
import RouterLink from '@/components/RouterLink'
import {
  USER_ROLE,
  hasUserGuideAccessByWl,
  useIsAdmin,
  useIsItPurchaser,
  useIsTelia,
  useIsThreeStepIT,
} from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { getAssetCount } from '@/modules/admin/resources/assets'
import { APP_ROUTES } from '@/util/app-routes'

import { NotificationCard } from './NotificationCard'
import { queries } from './queries'
import dashboardResources from './resources'

import './Dashboard.styles.scss'

export const Dashboard: FC = () => {
  const navigate = useNavigate()
  const profile = useProfile()
  const user = useUser()
  const isThreeStepIT = useIsThreeStepIT()
  const isTelia = useIsTelia()
  const isUserGuideVisible = hasUserGuideAccessByWl()

  const isAdmin = useIsAdmin()
  const isItPurchaser = useIsItPurchaser()
  const isOwnedAssetsForReturnCardVisible = Boolean(
    user &&
      [
        USER_ROLE.ADMIN,
        USER_ROLE.CUSTOMER_HEAD_ADMIN,
        USER_ROLE.CUSTOMER_ADMIN,
        USER_ROLE.IT_USER,
        USER_ROLE.DEPARTMENT_HEAD,
        USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD,
      ].includes(user.role)
  )

  useEffect(() => {
    if (isItPurchaser) {
      navigate(APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST)
    }
  }, [isItPurchaser, navigate])

  const { data, isLoading } = useQuery(queries.dashboardStatus(), dashboardResources.get)

  const { data: leaseEndingAssets, isLoading: isLoadingLeaseEndingAssets } = useQuery(
    queries.assetsToReturnStatus(),
    () =>
      getAssetCount({
        assetOwnership: 'LEASED',
        assetStatus: 'USE',
        leaseReturn: 90,
      })
  )
  const { data: nearLeaseEndAssets, isLoading: isLoadingNearLeaseEndAssets } = useQuery(
    queries.assetsNearLeaseEndStatus(),
    () =>
      getAssetCount({
        isNearLeaseEnd: true,
      })
  )
  const { data: pendingUserActionAssets, isLoading: isLoadingPendingUserActionAssets } = useQuery(
    queries.assetsPendingUserActionStatus(),
    () =>
      getAssetCount({
        assetStatus: 'REQ_SENT',
        activeEndOfLease: true,
      })
  )
  const { data: notConnectedLeaseEndingAssets, isLoading: isLoadingNotConnectedLeaseEndingAssets } =
    useQuery(queries.assetsToReturnNotConnectedStatus(), () =>
      getAssetCount({
        assetOwnership: 'LEASED',
        assetStatus: 'USE',
        leaseReturn: 90,
        notConnected: true,
      })
    )
  const { data: ownedAssetsForReturn, isLoading: isLoadingOwnedAssetsForReturn } = useQuery(
    queries.assetsOwnedForReturnStatus(),
    () =>
      getAssetCount({
        assetOwnership: 'OWNED',
        assetStatus: 'RETURN',
      }),
    {
      enabled: isOwnedAssetsForReturnCardVisible,
    }
  )

  if (
    isLoading ||
    isLoadingLeaseEndingAssets ||
    isLoadingPendingUserActionAssets ||
    isLoadingNearLeaseEndAssets ||
    isLoadingNotConnectedLeaseEndingAssets ||
    (isOwnedAssetsForReturnCardVisible && isLoadingOwnedAssetsForReturn)
  ) {
    return (
      <Container className="dashboard">
        <LoaderCircle />
      </Container>
    )
  }

  if (!data) return null

  const setUserGuideFileName = (): string => {
    if (isThreeStepIT) return GUIDE_FILE_PATHS.threeStepIT
    if (isTelia) return GUIDE_FILE_PATHS.telia
    return GUIDE_FILE_PATHS.default
  }
  const userGuideFileName = setUserGuideFileName()

  // General
  const { devicePolicy, salaryDeduction, injuryReport, ecom, tradeInOrders } = data
  const deviceOrderCount = devicePolicy.pendingApplicationCount
  const webshopOrderCount = ecom.pendingApplicationCount ?? 0
  const privateAppCount = salaryDeduction.pendingApplicationCount ?? 0
  const damageReportCount = injuryReport.pendingApplicationCount
  const tradeInOrdersCount = tradeInOrders.pendingActionsCount ?? 0

  // Leasing
  const leaseEndingCount = leaseEndingAssets ?? 0
  const pendingUserActionCount = pendingUserActionAssets ?? 0
  const notConnectedLeaseEndingCount = notConnectedLeaseEndingAssets ?? 0
  const assetsForReturnCount = ownedAssetsForReturn ?? 0
  const nearLeaseEndAssetsCount = nearLeaseEndAssets ?? 0

  const totalNotificationCount =
    deviceOrderCount +
    webshopOrderCount +
    privateAppCount +
    damageReportCount +
    leaseEndingCount +
    pendingUserActionCount +
    nearLeaseEndAssetsCount +
    notConnectedLeaseEndingCount +
    assetsForReturnCount +
    tradeInOrdersCount

  return (
    <Container className="flex flex-col">
      {totalNotificationCount > 0 && (
        <div className="dashboard-text">
          <Container.Header>
            <Translation
              id="Welcome, {userName}!"
              userName={
                profile?.name ? profile.name.charAt(0).toUpperCase() + profile.name.slice(1) : ''
              }
            />
          </Container.Header>
          <p className="dashboard-message">
            <Translation id="You have some new tasks to approve:" />
          </p>
        </div>
      )}
      <div className="mb-4 flex w-full flex-wrap justify-center gap-4 md:mb-0 md:justify-start md:gap-8">
        <NotificationCard
          to={APP_ROUTES.DEVICE_ORDERS}
          count={deviceOrderCount}
          cardTitle={<Translation id="Device Orders" />}
        />

        <NotificationCard
          // Status filter: Awaiting approval from manager
          to={`${APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST}?status=5`}
          count={webshopOrderCount}
          cardTitle={<Translation id="Webshop Orders" />}
        />

        <NotificationCard
          to={APP_ROUTES.EMPLOYEES_SALARY_DEDUCTION_APPLICATIONS}
          count={privateAppCount}
          cardTitle={<Translation id="Private Applications" />}
        />

        {profile?.hasInjuryReportsAccess && (
          <NotificationCard
            to={APP_ROUTES.HARDWARE_INJURY_REPORTS}
            count={damageReportCount}
            cardTitle={<Translation id="Damage Reports" />}
          />
        )}

        <NotificationCard
          to={`${APP_ROUTES.HARDWARE_ASSETS}?assetOwnership=LEASED&assetStatus=USE&leaseReturn=90`}
          count={leaseEndingCount}
          cardTitle={<Translation id="Lease Ending" />}
        />

        <NotificationCard
          to={`${APP_ROUTES.HARDWARE_ASSETS}?assetOwnership=LEASED&assetStatus=USE&leaseReturn=90&notConnected=true&sort=asc&sortBy=costObjectName`}
          count={notConnectedLeaseEndingCount}
          cardTitle={<Translation id="Lease Ending: Not connected to Employee" />}
        />

        <NotificationCard
          to={`${APP_ROUTES.HARDWARE_ASSETS}?assetStatus=REQ_SENT&activeEndOfLease=true`}
          count={pendingUserActionCount}
          cardTitle={<Translation id="Lease Ending: Pending User Action" />}
        />

        <NotificationCard
          to={`${APP_ROUTES.HARDWARE_ASSETS}?isNearLeaseEnd=true`}
          count={nearLeaseEndAssetsCount}
          cardTitle={<Translation id="Lease Ending: Immediate Action Required" />}
        />

        {isOwnedAssetsForReturnCardVisible && (
          <NotificationCard
            to={`${APP_ROUTES.HARDWARE_ASSETS}?assetOwnership=OWNED&assetStatus=RETURN`}
            count={assetsForReturnCount}
            cardTitle={<Translation id="Assets for Return" />}
          />
        )}

        <NotificationCard
          to={APP_ROUTES.HARDWARE_TRADE_IN}
          count={tradeInOrdersCount}
          cardTitle={<Translation id="Trade-in orders" />}
        />

        {totalNotificationCount === 0 && (
          <div className="mobile-adjusted-text">
            <Container.Header>
              <Translation
                id="Welcome, {userName}!"
                userName={
                  profile?.name ? profile.name.charAt(0).toUpperCase() + profile.name.slice(1) : ''
                }
              />
            </Container.Header>
            <p className="dashboard-message">
              <Translation id="Good job, you have no outstanding tasks." />
            </p>
          </div>
        )}
      </div>
      {user?.role !== USER_ROLE.REPORT_USER && (
        <div className="ml-4 mt-4 flex flex-row flex-wrap items-stretch justify-center gap-3 md:ml-0 md:mr-0 md:flex-row md:justify-start">
          <ChevronLink
            to={APP_ROUTES.HARDWARE_SHOP}
            colorMode={isThreeStepIT ? 'dark-gray' : 'gray'}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-44 min-w-44 items-center justify-between gap-6 text-ellipsis whitespace-nowrap rounded-md md:gap-3"
          >
            <Icon>
              <CartIcon className="ml-2 h-5 w-5" />
            </Icon>
            <Translation id="New Order" className="text-ellipsis whitespace-nowrap" />
          </ChevronLink>

          <ChevronLink
            to={APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST}
            colorMode={isThreeStepIT ? 'dark-gray' : 'gray'}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-44 min-w-44 items-center justify-between gap-3 text-ellipsis whitespace-nowrap rounded-md"
          >
            <Icon>
              <PhonePlusIcon className="ml-1.5 h-5 w-5" />
            </Icon>
            <Translation id="New Subscription" className="text-ellipsis whitespace-nowrap" />
          </ChevronLink>

          <ChevronLink
            to={APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST}
            colorMode={isThreeStepIT ? 'dark-gray' : 'gray'}
            className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-44 min-w-44 items-center justify-between gap-2 text-ellipsis whitespace-nowrap rounded-md"
          >
            <Icon>
              <PhoneOffIcon className="ml-2 mr-0.5 h-5 w-5" />
            </Icon>
            <Translation id="Terminations" className="text-ellipsis whitespace-nowrap" />
          </ChevronLink>
          {isAdmin && (
            <ChevronLink
              to={APP_ROUTES.ADMIN_SMS_SENDER}
              colorMode={isThreeStepIT ? 'dark-gray' : 'gray'}
              className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-44 min-w-44 items-center justify-between gap-3 text-ellipsis whitespace-nowrap rounded-md"
            >
              <Icon>
                <MessageTextOutlineIcon className="ml-2 mr-0 h-5 w-5" />
              </Icon>
              <Translation id="SMS" className="mr-12 text-ellipsis whitespace-nowrap" />
            </ChevronLink>
          )}
          {isUserGuideVisible && (
            <ExternalLink
              className="dashboard-notification-manual-link shadow-basic flex min-h-[60px] w-44 min-w-44 items-center justify-between gap-1 text-ellipsis whitespace-nowrap rounded-md text-sm"
              hasUnderline={false}
              linkColorMode="gray"
              href={userGuideFileName}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon>
                <FileDocumentOutlineIcon className="mx-2 h-5 w-5" />
              </Icon>
              <Translation
                id="See user manual"
                className="text-ellipsis whitespace-nowrap tracking-tight"
              />
              <Icon className="mr-1">
                <ChevronRightIcon />
              </Icon>
            </ExternalLink>
          )}
        </div>
      )}
    </Container>
  )
}

const ChevronLink: FC<
  PropsWithChildren<{ to: string; className?: string; colorMode?: ColorMode }>
> = ({ to, children, className, colorMode }) => (
  <RouterLink to={to} className={className} hasUnderline={false} colorMode={colorMode}>
    {children}
    <Icon className="mr-1">
      <ChevronRightIcon />
    </Icon>
  </RouterLink>
)
